import { LuAlertCircle, LuCheckCircle } from 'react-icons/lu';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { ProductTypeResponseDTO } from '../../DTO/productResponseDTO';
import { ToastCart } from '../../components/ToastCart';
import { DateUtils } from '../../helpers/dateUtils';
import { WaveLevelEnum } from '../../helpers/utils';
import { validateCouponAgainstCartItems } from '../../helpers/couponValidation';
import { InputCheckoutProps } from '../../@types/ServiceCheckoutInput';
import { toast } from 'react-toastify';
import { isUndecidedDate } from '../../components/Calendar';
import { LogInResponseDTO } from '@/DTO/authDTO';
import { getCampaign } from '@/services/salesShopItems.service';
import { AppliedCouponResponseDTO } from '@/DTO/checkoutResponseDTO';

export enum CartServiceType {
    Waves = 'waves',
    Tickets = 'tickets',
    Park = 'park',
    Services = 'services',
    Sports = 'sports',
    Others = 'others',
}

export type CartValuesServiceStore = {
    id: string;
    title: string;
    value: number;
    date: Date;
    type: CartServiceType;
    idCart?: string;
    scheduleId?: number;
    scheduleHour?: string;
    waveLevel?: WaveLevelEnum;
    client?: {
        name: string;
        document: string;
        foreigner?: boolean;
    };
    available?: number;
    waveSide?: 'Left' | 'Right';
    isFirstHeat?: boolean;
    giftId?: number;
    campaignId?: number;
    discountValue?: number;
    usedCampaignRuleId?: number;
    freeSession?: boolean;
};

type State = {
    cartValues: CartValuesServiceStore[] | null;
    cartIsVisible: boolean;
    currentIdRemoving: string | null;
    nameRemovingElement: string;
    typesList: ProductTypeResponseDTO[] | null;
    scheduleId: number | null;
    currentDate: Date;
    checkoutId: number | null;
    address: InputCheckoutProps['address'] | null;
    isPaymentLinkCart: boolean;
    coupon: AppliedCouponResponseDTO | null;
};

type Action = {
    setCartValues: (
        cartValues: CartValuesServiceStore,
        showToast?: boolean
    ) => void;
    removeCartValue: (id: string, scheduleId?: number, accessProfile?: string) => void;
    cleanCart: () => void;
    setCartIsVisible: (cartIsVisible: boolean) => void;
    setCurrentIdRemoving: (currentIdRemoving: string | null) => void;
    setTypesList: (typesList: ProductTypeResponseDTO[] | undefined) => void;
    editCartValue: (
        id: string,
        newValue: CartValuesServiceStore,
        disableToast?: boolean
    ) => void;
    setCurrentDate: (date: Date) => void;
    setCheckoutId: (checkoutId: number | null) => void;
    setAddress: (address: InputCheckoutProps['address']) => void;
    updateSpecificCartValue: (
        id: string,
        newValue: Partial<CartValuesServiceStore>
    ) => void;
    updateItemPrice: (id: string, price: number, date: string) => void;
    setCartProducts: (cartProducts: CartValuesServiceStore[]) => void;
    setIsPaymentLinkCart: (isPaymentLink: boolean) => void;
    setCoupon: (coupon: AppliedCouponResponseDTO) => void;
    removeCoupon: () => void;
};

export const useShoppingCartStore = create<State & Action>((set, get) => ({
    cartValues: null,
    cartIsVisible: false,
    currentIdRemoving: null,
    nameRemovingElement: '',
    typesList: null,
    scheduleId: null,
    currentDate: new Date(),
    checkoutId: null,
    address: null,
    isPaymentLinkCart: false,
    coupon: null,

    setCheckoutId: checkoutId => {
        set(() => ({
            checkoutId,
        }));
    },

    setTypesList: typesList => {
        set(() => ({
            typesList,
        }));
    },

    setAddress: address => {
        set(() => ({
            address,
        }));
    },

    setCurrentDate: date => {
        set(() => ({
            currentDate: date,
        }));
    },

    setCurrentIdRemoving: currentIdRemoving => {
        set(() => ({
            currentIdRemoving,
        }));

        const { cartValues } = get();
        if (!currentIdRemoving || !cartValues?.length) {
            set(() => ({
                nameRemovingElement: '',
                scheduleId: null,
            }));
            return;
        }

        const elementShop = cartValues.find(
            item => item.idCart === currentIdRemoving
        );

        if (elementShop) {
            const hourDate = `${elementShop?.scheduleHour ? 'às ' + elementShop.scheduleHour : ''}`;
            const longStringDate = isUndecidedDate(elementShop.date)
                ? 'sem data definida'
                : `agendado para ${DateUtils.formatToLargeStringDate(elementShop.date)} ${hourDate}`;
            const nameTicketRemove = `${elementShop.title} ${longStringDate}`;
            set(() => ({
                nameRemovingElement: nameTicketRemove,
                scheduleId: elementShop?.scheduleId || null,
            }));
        }
    },

    setCartValues: (cartValue, showToast = true) => {
        const { cartValues: currentCartValues } = get();

        // Se houver um cupom aplicado que restrinja por waveLevel, valida antes de adicionar
        const { coupon } = get();
        if (coupon && coupon.applyToWaveLevel) {
            const prospectiveCart = [...(currentCartValues || []), cartValue];
            const validation = validateCouponAgainstCartItems(coupon, prospectiveCart);
            if (!validation.isValid) {
                ToastCart({
                    Icon: LuAlertCircle,
                    date: cartValue.date,
                    title: cartValue.title,
                    hour: cartValue?.scheduleHour,
                    typeAction: 'warn',
                    message: validation.errorMessage,
                });
                return;
            }
        }

        if (!currentCartValues) {
            
            // Se não há valores atuais e há um novo valor, inicializa o array com o novo valor
            set(() => ({
                cartValues: [{ ...cartValue, idCart: uuidv4() }],
            }));

            if (showToast) {
                ToastCart({
                    Icon: LuCheckCircle,
                    date: cartValue.date,
                    title: cartValue.title,
                    hour: cartValue?.scheduleHour,
                    typeAction: 'add',
                });
            }
        } else if (currentCartValues) {
            //Se tentar adicionar no carrinho um produto que já está
            const findCurrentValue = currentCartValues.filter(
                item =>
                    item.id === cartValue.id &&
                    item.scheduleId === cartValue.scheduleId
            );

            const isExceded =
                !!cartValue?.available &&
                findCurrentValue.length === cartValue.available;

            if (!isExceded) {
                // Se há valores atuais e um novo valor, adiciona o novo valor ao array existente
                set(() => ({
                    cartValues: [
                        ...currentCartValues,
                        { ...cartValue, idCart: uuidv4() },
                    ],
                }));

                if(!showToast) return
                ToastCart({
                    Icon: LuCheckCircle,
                    date: cartValue.date,
                    title: cartValue.title,
                    hour: cartValue?.scheduleHour,
                    typeAction: 'add',
                });
            } else {
                if (showToast) {
                    ToastCart({
                        Icon: LuAlertCircle,
                        date: cartValue.date,
                        title: cartValue.title,
                        typeAction: 'warn',
                    });
                }
            }
        }
    },

    removeCartValue: (id, scheduleId, accessProfile) => {
        const { cartValues } = get();

        if (cartValues) {
            const valueToRemove = cartValues.find(
                item => item.idCart === id && item.scheduleId === scheduleId
            );
            const indexToRemove = cartValues.findIndex(
                item => item.idCart === valueToRemove?.idCart
            );

            if (indexToRemove !== -1 && valueToRemove) {
                const cartValuesRemoved = cartValues.filter(
                    (_, index) => index !== indexToRemove
                );

                set(() => ({
                    cartValues: cartValuesRemoved,
                }));

                ToastCart({
                    Icon: LuCheckCircle,
                    date: valueToRemove.date,
                    title: valueToRemove.title,
                    hour: valueToRemove?.scheduleHour,
                    typeAction: 'remove',
                });
            }
        }
        
        calculateCartCampaignDiscount(accessProfile);
    },

    cleanCart: () => {
        set(() => ({
            cartValues: null,
            isPaymentLinkCart: false,
        }));
    },

    setCartIsVisible(cartIsVisible) {
        set(() => ({
            cartIsVisible,
        }));
    },

    editCartValue: (id, newValue, disableToast) => {
        const { cartValues: currentCartValues } = get();

        if (currentCartValues) {
            const index = currentCartValues.findIndex(
                item => item.idCart === id
            );

            if (index !== -1) {
                const updatedCartValues = [...currentCartValues];
                const oldObject = updatedCartValues[index];

                const newObject = {
                    ...oldObject,
                    ...newValue,
                };

                updatedCartValues[index] = {
                    ...oldObject,
                    ...newValue,
                };

                set(() => ({
                    cartValues: updatedCartValues,
                }));

                if (JSON.stringify(oldObject) === JSON.stringify(newObject)) {
                    return toast.warn('Nenhuma edição feita no produto');
                }

                {
                    !disableToast &&
                        ToastCart({
                            Icon: LuCheckCircle,
                            typeAction: 'edit',
                        });
                }
            } else {
                {
                    !disableToast &&
                        ToastCart({
                            Icon: LuAlertCircle,
                            typeAction: 'warn',
                        });
                }
            }
        } else {
            ToastCart({
                Icon: LuAlertCircle,
                typeAction: 'error',
            });
        }
    },
    updateSpecificCartValue: (id, newValue) =>
        set(state => {
            const newCartValues = state.cartValues?.map(item => {
                if (item.id === id) {
                    return {
                        ...item,
                        ...newValue,
                    };
                }
                return item;
            });

            return { ...state, cartValues: newCartValues };
        }),
    updateItemPrice: (itemId, newPrice, targetDate) =>
        set(state => {
            const newCartValues = state.cartValues?.map(item => {
                const isItemGift = typeof item.giftId === 'number';
                const isFreeReservationSession = item.freeSession === true;
                const isTargetItem = item.id === itemId;
                const isSameDate =
                    DateUtils.formatToInitalYear(item.date) === targetDate;

                if (isTargetItem && isSameDate && isFreeReservationSession) {
                    return item;
                }

                // Não atualiza preço de se for um Gift
                if (item.id === itemId && !isItemGift && DateUtils.formatToInitalYear(item.date) === targetDate) {
                    return {
                        ...item,
                        value: newPrice,
                    };
                }
                return item;
            });

            return { ...state, cartValues: newCartValues };
        }),
    setCartProducts: products =>
        set(state => ({ ...state, cartValues: products })),
    setIsPaymentLinkCart: isPaymentLink =>
        set(state => ({ ...state, isPaymentLinkCart: isPaymentLink })),
    setCoupon: coupon => {
        set(state => ({ ...state, coupon }));
    },
    removeCoupon: () => {
        set(state => ({ ...state, coupon: null }));
    },
}));

// Gifts não expirados
export const getValidUserGifts = (user?: LogInResponseDTO) => {
    if (!user || !user.gifts) return [];
    const validGifts = user.gifts.filter(
        d => d.giftType.active && !d.isExpired
    );
    return validGifts;
};

export const getGiftsUsage = (
    cartItems: CartValuesServiceStore[]
) => {
    const cartProductsUsingGift = cartItems.filter(
        d => typeof d.giftId === 'number' && d.isFirstHeat
    );
    const giftsUsageById = new Map<number, number>();

    cartProductsUsingGift.forEach(product => {
        if (typeof product.giftId === 'number') {
            const giftId = product.giftId;

            if (giftsUsageById.has(giftId)) {
                giftsUsageById.set(giftId, giftsUsageById.get(giftId)! + 1);
            } else {
                giftsUsageById.set(giftId, 1);
            }
        }
    });

    return giftsUsageById;
};

// Gifts ainda não resgatados
/**
 * Retorna uma lista de todos os gifts que ainda não foram resgatados
 * @description Verifica todos os itens no carrinho e todos os gifts do usuário e retorna apenas os ainda disponíveis
 * @param {LogInResponseDTO} user
 * @returns {*}
 */
export const getAvailableGifts = (user?: LogInResponseDTO) => {
    const cartItems = useShoppingCartStore.getState().cartValues ?? [];
    const validGifts = getValidUserGifts(user);
    const giftsUsageById = getGiftsUsage(cartItems);

    const notYetUsedGifts = validGifts.filter(gift => {
        const usageCount = giftsUsageById.get(gift.id) ?? 0;
        return usageCount < gift.availableAmount;
    });

    return notYetUsedGifts;
};

//
/**
 * Retorna produtos do carrinho que estão com gifts válidos
 * @description Útil para quando o usuário faz logout ou login
 * @param {LogInResponseDTO} user
 * @returns {CartValuesServiceStore[]}
 */
export const getValidProductGiftsInCart = (
    user?: LogInResponseDTO
): CartValuesServiceStore[] => {
    const userValidGifts = getValidUserGifts(user);
    const cartItems = useShoppingCartStore.getState().cartValues ?? [];

    const validItems = cartItems.filter(item => {
        const isGift = typeof item.giftId === 'number';
        if (!isGift) return true;
        const isValidGift = userValidGifts.some(
            gift => gift.id === item.giftId
        );
        return isGift && isValidGift;
    });

    return validItems;
};


export async function calculateCartCampaignDiscount( accessProfileName?: string){
    const campaign = await getCampaign(new Date().toISOString())
    if(!campaign) return
    
    if(
        accessProfileName != "OpenSea"
        && accessProfileName != "OpenSeaPlus"
        && accessProfileName != "Guest"
    ) return;

    const cartItems : CartValuesServiceStore[] = useShoppingCartStore.getState().cartValues ?? []; 
    const setCartItems = useShoppingCartStore.getState().setCartProducts; 

    let ruleDiscountAmount = 0

    campaign.rules.forEach(rule => {
        if(!rule.active) return;
        
        const updatedCartItems = cartItems.map(item => {
            if(
                item.type !== "waves" ||
                item.waveLevel !== 'Reef' ||
                item.date > new Date(rule.benefitExpiresOn) ||
                diffDaysInclusive(new Date(), item.date) < rule.purchasedAheadDays
            ) {
                return {
                    ...item,
                    campaignId: undefined,
                    usedCampaignRuleId: undefined,
                    discountValue: undefined
                }
            }

            ruleDiscountAmount += rule.benefitValue;

            return {
                ...item,
                campaignId: campaign.id,
                usedCampaignRuleId: rule.id,
                discountValue: rule.benefitValue
            }
        })

        setCartItems(updatedCartItems);
    });

    if(ruleDiscountAmount > 0) {
        toast.success(
            () => {
                return (
                    <>
                        Um total de 
                        <strong> R${ruleDiscountAmount.toFixed(2)}</strong>
                        {' '} de desconto da campanha
                        <strong> {campaign.name}</strong>{' '}
                    </>
                );
            },
        );
    }
}

function diffDaysInclusive(start: Date, end: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;

  const startDate = new Date(start);
  const endDate = new Date(end);

  startDate.setHours(0,0,0,0);
  endDate.setHours(0,0,0,0);

  return Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay) + 1;
}